<script setup>
import { ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Modal, Select } from '@codinglabsau/ui'
import { FormBuilder, useSchema } from '@codinglabsau/inertia-form-builder'
import {
  FacebookIcon,
  GooglePlusIcon,
  InstagramIcon,
  LinkedinIcon,
  RedditIcon,
  TiktokIcon,
  TwitterIcon,
} from '@/Components'

const showLocaleSettings = ref(false)

const supportedLanguages = usePage().props.supported_languages
  ? Object.entries(usePage().props.supported_languages).map(([id, name]) => ({ id, name }))
  : []

const supportedCurrencies = usePage().props.supported_currencies?.map(currency => ({
    id: currency,
    name: currency,
  }))

const localeSchema = useSchema({
  language: {
    component: Select,
    value: usePage().props.session.language ?? 'en',
    description: __('Please note non-English language support is experimental.'),
    props: {
      options: supportedLanguages,
    },
  },
  currency: {
    component: Select,
    value: usePage().props.session.currency_code,
    description: __('Prices and payments are always calculated in :base_currency_code. The selected currency will be utilised to display estimated prices on certain pages of the website where indicated.', { base_currency_code: usePage().props.tenant.settings.currency_code }),
    props: {
      options: supportedCurrencies,
    },
  },
})

const submit = () => {
  localeSchema.form.post(route('locale-settings.update'), {
    onSuccess: () => showLocaleSettings.value = false,
  })
}
</script>

<template>
  <div class="border-t border-gray-200 bg-gray-100 flex flex-col items-center relative py-6 gap-2 text-gray-700">
    <div class="flex sm:flex-row flex-col items-center justify-center gap-4">
      <div class="flex">
        <button
          @click="showLocaleSettings = true"
          class="bg-gray-200 border-t border-b border-l border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs rounded-l hover:bg-gray-300"
        >
          {{ $page.props.supported_languages[$page.props.session.language] }}
        </button>

        <button
          @click="showLocaleSettings = true"
          class="bg-gray-200 border border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs rounded-r hover:bg-gray-300">
          {{ $page.props.session.currency_code }}
        </button>
      </div>

      <div class="flex">
        <a
          v-if="$page.props.auth"
          :href="route('account.dashboard')"
          class="bg-gray-200 border-t border-b border-l border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs rounded-l hover:bg-gray-300"
        >
          {{ __('My account') }}
        </a>

        <div v-else class="flex">
          <a
            :href="route('register')"
            class="bg-gray-200 flex items-center border-t border-b border-l border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs rounded-l hover:bg-gray-300"
          >
            {{ __('Register') }}
          </a>
          <a
            :href="route('login')"
            class="bg-gray-200 flex items-center border-l border-t border-b border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs hover:bg-gray-300"
          >
            {{ __('Log in') }}
          </a>
        </div>

        <a
          :href="route('help.index')"
          class="flex items-center bg-gray-200 border border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs hover:bg-gray-300"
        >
          {{ __('Help & FAQ') }}
        </a>
        <a
          v-if="$page.props.auth"
          :href="route('account.support.tickets.index')"
          class="flex items-center bg-gray-200 border-t border-b border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs hover:bg-gray-300"
        >
          {{ __('Support') }}
        </a>
        <a
          :href="route('sitemap')"
          class="flex items-center bg-gray-200 border border-gray-300 py-2 sm:px-4 px-2 my-0 sm:my-1 text-xs rounded-r hover:bg-gray-300"
        >
          {{ __('Sitemap') }}
        </a>
      </div>
    </div>

    <!-- Social links -->
    <div class="flex items-center justify-center text-blue-500 gap-6">
      <a
        v-if="$page.props.tenant.settings.facebook_url"
        :href="$page.props.tenant.settings.facebook_url"
        class="hover:text-gray-900"
      >
        <FacebookIcon class="w-6 h-6" />
      </a>
      <a
        v-if="$page.props.tenant.settings.instagram_url"
        :href="$page.props.tenant.settings.instagram_url"
        class="hover:text-gray-900"
      >
        <InstagramIcon class="w-6 h-6" />
      </a>
      <a
        v-if="$page.props.tenant.settings.twitter_url"
        :href="$page.props.tenant.settings.twitter_url"
        class="hover:text-gray-900"
      >
        <TwitterIcon class="w-6 h-6" />
      </a>
      <a
        v-if="$page.props.tenant.settings.googleplus_url"
        :href="$page.props.tenant.settings.googleplus_url"
        class="hover:text-gray-900"
      >
        <GooglePlusIcon class="w-6 h-6" />
      </a>
      <a
        v-if="$page.props.tenant.settings.reddit_url"
        :href="$page.props.tenant.settings.reddit_url"
        class="hover:text-gray-900"
      >
        <RedditIcon class="w-6 h-6" />
      </a>
      <a
        v-if="$page.props.tenant.settings.linkedin_url"
        :href="$page.props.tenant.settings.linkedin_url"
        class="hover:text-gray-900"
      >
        <LinkedinIcon class="w-5 h-5" />
      </a>
      <a
        v-if="$page.props.tenant.settings.tiktok_url"
        :href="$page.props.tenant.settings.tiktok_url"
        class="hover:text-gray-900"
      >
        <TiktokIcon class="w-5 h-5" />
      </a>
    </div>

    <div class="flex text-blue-500 text-xs gap-4">
      <a
        class="hover:underline hover:text-blue-700"
        href="/help/legal/privacy-policy"
      >
        {{ __('Privacy Policy') }}
      </a>
      <a
        class="hover:underline hover:text-blue-700"
        href="/help/legal/terms-of-use"
      >
        {{ __('Terms and Conditions') }}
      </a>
      <a
        class="hover:underline hover:text-blue-700"
        href="https://status.liveplatforms.com"
        target="_blank"
      >
        {{ __('Status') }}
      </a>
    </div>

    <div class="text-xs">
      © 2004-{{ moment().format('Y') }} Live Platforms {{ __('All Rights Reserved') }}
    </div>

    <Modal :open="showLocaleSettings" @close="showLocaleSettings = false">
      <template #header> {{ __('Locale Settings') }}</template>

      <form @submit.prevent="submit">
        <FormBuilder :schema="localeSchema" />
      </form>
    </Modal>
  </div>
</template>
